import React from "react";
import { useTranslation } from "../localization/useTranslation";
import { Box, CircularProgress, IconButton, TableCell, TableRow } from "@mui/material";
import {
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalTableWithControls,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { useBestPracticeDocumentTemplates } from "./BestPracticeDocumentTemplates.hooks";
import { usePanes } from "../main/panes/usePanes";
import DocumentPane from "../main/panes/documentPane/DocumentPane";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { BestPracticeDocumentTemplateTableModel, BestPracticeDocumentTemplateViewModel } from "./BestPracticeDocumentTemplates.types";
import AddBestPracticeDocumentTemplateDialog from "./addBestPracticeDocumentTemplateDialog/AddBestPracticeDocumentTemplateDialog";
import EditBestPracticeDocumentTemplateDialog from "./editBestPracticeDocumentTemplateDialog/EditBestPracticeDocumentTemplateDialog";
import DeleteBestPracticeDocumentTemplateDialog from "./deleteBestPracticeDocumentTemplateDialog/DeleteBestPracticeDocumentTemplateDialog";
import EmptyStateIcon from "../common/svgs/emptyState.svg?react";
import { download, eye } from "@common/icons.tsx";
import { isPreviewSupported } from "@common/components/documentPreview/DocumentPreview.hooks.ts";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function BestPracticeDocumentTemplates() {
    const { translateString, translateDate } = useTranslation();
    const { permissions } = useUserContext();
    const {
        breadCrumbs,
        isLoading,
        data,
        showAddDialog,
        setShowAddDialog,
        selectedTemplate,
        setSelectedTemplate,
        showEditDialog,
        setShowEditDialog,
        showDeleteDialog,
        setShowDeleteDialog,
        onTemplateSaved,
        onTemplateDeleted,
        downloadingDocument,
        onDownloadClick,
        getBestPracticeDocumentBlobDocumentTemplateUrl,
    } = useBestPracticeDocumentTemplates();
    const { open: openPane } = usePanes();

    function openPreview(template: BestPracticeDocumentTemplateViewModel) {
        openPane(DocumentPane, {
            documents: [
                {
                    documentId: template.id,
                    dataId: template.blobId,
                    name: template.name,
                    fileName: template.fileName,
                    customDocumentUrl: getBestPracticeDocumentBlobDocumentTemplateUrl(template),
                },
            ],
        });
    }

    const getMenuItems = (template: BestPracticeDocumentTemplateViewModel) => {
        if (permissions.canManageMasterData) {
            return [
                {
                    menuItem: translateString("edit"),
                    onClick: () => {
                        setSelectedTemplate(template);
                        setShowEditDialog(true);
                    },
                },
                {
                    menuItem: translateString("delete"),
                    onClick: () => {
                        setShowDeleteDialog(template);
                    },
                },
            ];
        }

        return [];
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    permissions.canManageMasterData ? (
                        <DotLegalButton buttonType={"primary"} size="medium" onClick={() => setShowAddDialog(true)}>
                            {translateString("createBestPracticeDocumentTemplate")}
                        </DotLegalButton>
                    ) : undefined
                }
            />
            <TablePagePaper>
                {isLoading || (data && data.length > 0) ? (
                    <DotLegalTableWithControls
                        internalScroll
                        headers={getHeaders(translateString)}
                        getUserSpecificPageLength={() => 30}
                        hideRowsPerPage
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        clickableRows={false}
                        hasOverflowMenu
                        labelRowsPerPage={translateString("showEntites")}
                        labelSearch={translateString("search")}
                        noOptionsLabel={translateString("noOptions")}
                        paginationLabelOf={translateString("labelOf")}
                        emptyText={translateString("noData")}
                        data={getTableData()}
                        renderRow={(row, i) => (
                            <TableRow key={row.id}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.fileName}</TableCell>
                                <TableCell>{translateDate(row.created)}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {row.id === downloadingDocument ? (
                                            <CircularProgress sx={{ marginLeft: 1.2, marginTop: "4px" }} size={20} />
                                        ) : (
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDownloadClick(row);
                                                }}
                                            >
                                                {download}
                                            </IconButton>
                                        )}
                                        {isPreviewSupported(row.fileName!) && (
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openPreview(row);
                                                }}
                                            >
                                                {eye}
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell align={"right"}>
                                    <DotLegalOverflowMenu items={getMenuItems(row)} />
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<EmptyStateIcon />} text={translateString("noBestPracticeDocumentTemplatesEmptyState")} />
                )}
            </TablePagePaper>
            {showEditDialog && selectedTemplate && (
                <EditBestPracticeDocumentTemplateDialog
                    onDialogClose={() => {
                        setSelectedTemplate(undefined);
                        setShowEditDialog(false);
                    }}
                    onSave={onTemplateSaved}
                    selectedTemplate={selectedTemplate}
                />
            )}
            {showDeleteDialog && (
                <DeleteBestPracticeDocumentTemplateDialog
                    onDialogClose={() => setShowDeleteDialog(undefined)}
                    onDeleted={onTemplateDeleted}
                    selectedTemplate={showDeleteDialog}
                />
            )}
            {showAddDialog && <AddBestPracticeDocumentTemplateDialog onDialogClose={() => setShowAddDialog(false)} onSave={onTemplateSaved} />}
        </Box>
    );

    function getTableData(): Array<BestPracticeDocumentTemplateTableModel> {
        return (data ?? []).map((x) => {
            return {
                id: x.id,
                name: x.name,
                fileName: x.fileName,
                created: x.created,
                createdString: translateDate(x.created),
                blobId: x.blobId,
            };
        });
    }
}

export default BestPracticeDocumentTemplates;

function getHeaders(translateString: any) {
    let headers = Array<ITableHeader<BestPracticeDocumentTemplateTableModel>>();
    headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
    headers.push({ property: "fileName", text: translateString("fileName"), align: "left", showOnMobile: true });
    headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });
    headers.push({ property: "id", text: translateString("accessDocument") });

    return headers;
}
